import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ShoppingCart, Check } from 'lucide-react'

const ModalOffersDemoPage: React.FC = () => {
  const navigate = useNavigate()
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [showingModal, setShowingModal] = useState(false)
  const [productAdded, setProductAdded] = useState(false)
  const [modalClosedByUser, setModalClosedByUser] = useState(false)

  // Sample product data
  const product = {
    name: "Premium Wireless Headphones",
    price: 149.99,
    image: "🎧",
    rating: 4.8,
    reviews: 324
  }

  useEffect(() => {
    
    // Load and initialize the MomentScience SDK
    const loadMomentScienceSDK = async () => {
      try {
        // Configure MomentScience SDK
        window.AdpxConfig = {
          theme_id: 'Demo-Labs-Modal-Offers', // Updated theme ID as requested
          accountId: 'ffa59da09972e55e', // Demo account ID
          autoShow: false, // Important: We'll show the modal manually
          settings: {
            multi_offer_unit: true,
            ad_position: 'center' // Required for modal view
          }
        }

        // Set user information
        window.AdpxUser = {
          customerType: 'tech_enthusiast',
          pageType: 'product_detail',
          country: 'us',
          pub_user_id: 'MomentScience_Modal_Demo',
          theme_id: 'Demo-Labs-Modal-Offers', // Updated theme ID as requested
          item_name: "Premium Wireless Headphones" // Added item name for better offer targeting
        }

        // Remove any existing SDK script
        const existingScript = document.getElementById('adpx-launcher')
        if (existingScript) {
          existingScript.remove()
        }

        // Load the SDK script
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = 'https://cdn.pubtailer.com/launcher.min.js'
        script.crossOrigin = 'anonymous'
        script.async = true
        script.id = 'adpx-launcher'
        document.head.appendChild(script)

        // Wait for SDK to load and initialize
        await new Promise((resolve) => {
          script.onload = () => {
            // Initialize SDK once loaded
            if (window.Adpx) {
              try {
                console.log('📝 Config before init:', window.AdpxConfig)
                console.log('👤 User data before init:', window.AdpxUser)
                
                window.Adpx.init(window.AdpxConfig)
                console.log('🚀 MomentScience SDK initialized successfully with modal settings')
                
                // Debug available methods
                console.log('🔍 Available SDK methods:', 
                  Object.getOwnPropertyNames(window.Adpx)
                    .filter(prop => typeof (window.Adpx as any)[prop] === 'function')
                )
                
                // Add a listener for new elements being added to detect when modal is created
                const bodyObserver = new MutationObserver((mutations) => {
                  mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length) {
                      mutation.addedNodes.forEach((node) => {
                        if ((node as Element).classList && (node as Element).classList.contains('adpx-unit')) {
                          console.log('✅ MomentScience modal element detected:', node);
                        }
                      });
                    }
                  });
                });
                
                // Start observing the document body
                bodyObserver.observe(document.body, { childList: true, subtree: true });
                
                // Clean up observer after 60 seconds
                setTimeout(() => bodyObserver.disconnect(), 60000);
                
                setSdkLoaded(true)
                resolve(true)
              } catch (error) {
                console.error('❌ Error initializing MomentScience SDK:', error)
                setSdkLoaded(true)
                resolve(true)
              }
            } else {
              console.warn('⚠️ MomentScience SDK not available - using demo mode')
              setSdkLoaded(true)
              resolve(true)
            }
          }
          
          script.onerror = () => {
            console.warn('⚠️ Failed to load MomentScience SDK')
            setSdkLoaded(true)
            resolve(true)
          }
        })

      } catch (error) {
        console.warn('⚠️ SDK initialization error:', error)
        setSdkLoaded(true)
      }
    }

    loadMomentScienceSDK()

    // Cleanup
    return () => {
      const script = document.getElementById('adpx-launcher')
      if (script) {
        script.remove()
      }
      // Clean up global variables
      if (window.AdpxConfig) delete window.AdpxConfig
      if (window.AdpxUser) delete window.AdpxUser
    }
  }, [])

  // Function to check if modal content has appeared
  const checkForModalContent = () => {
    const modalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Check if any of the added nodes is the modal
          const adpxUnit = document.querySelector('.adpx-unit');
          if (adpxUnit) {
            console.log('🔍 MomentScience modal content detected in DOM');
            // No styling adjustments - let the SDK control the display
            modalObserver.disconnect();
          }
        }
      });
    });
    
    // Start observing the document for added nodes
    modalObserver.observe(document.body, { childList: true, subtree: true });
    
    // Safety timeout - stop observer after 5 seconds regardless
    setTimeout(() => {
      modalObserver.disconnect();
    }, 5000);
    
    return modalObserver;
  };

  // Effect to prevent body scrolling when modal is open
  useEffect(() => {
    if (showingModal) {
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Allow scrolling when modal is closed
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      // Reset scrolling when component unmounts
      document.body.style.overflow = 'auto';
    };
  }, [showingModal]);

  // Add product to cart and show modal
  const handleAddToCart = () => {
    if (!sdkLoaded) {
      console.warn('SDK not loaded yet')
      return
    }

    setProductAdded(true)
    
    // Show success message briefly before showing modal
    setTimeout(() => {
      // Show the modal with MomentScience offers
      if (window.Adpx && !modalClosedByUser) {
        try {
          console.log('🚀 Attempting to show MomentScience modal')
          
          // Set up observer to detect when modal content appears
          checkForModalContent();
          
          window.Adpx.show()
          setShowingModal(true)
          console.log('🎉 Displaying MomentScience modal')
          
          // Listen for potential external close events
          const checkModalInterval = setInterval(() => {
            // If the SDK closed the modal by itself, update our state
            const modalExists = document.querySelector('.adpx-unit')
            if (!modalExists && showingModal) {
              setShowingModal(false)
              clearInterval(checkModalInterval)
            }
          }, 500)
          
          // Clean up interval after 30 seconds
          setTimeout(() => clearInterval(checkModalInterval), 30000)
        } catch (error) {
          console.error('❌ Error showing MomentScience modal:', error)
        }
      }
    }, 1000)
  }

  // Listen for modal closing events from the SDK
  useEffect(() => {
    if (showingModal) {
      // Set up an interval to check if the modal was closed by the SDK's close button
      const checkModalInterval = setInterval(() => {
        const modalExists = document.querySelector('.adpx-unit')
        if (!modalExists && showingModal) {
          console.log('👋 Modal closed by SDK')
          setShowingModal(false)
          setModalClosedByUser(true)
          clearInterval(checkModalInterval)
        }
      }, 500)
      
      // Clean up interval after 30 seconds
      setTimeout(() => clearInterval(checkModalInterval), 30000)
      
      return () => {
        clearInterval(checkModalInterval)
      }
    }
  }, [showingModal])

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/moments-gallery')}
              className="flex items-center space-x-2 px-4 py-2 bg-white shadow-sm hover:shadow-md text-[#6E6D7A] hover:text-[#1A2B49] rounded-lg transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Gallery</span>
            </button>
            <div className="h-8 w-px bg-[#F3F3F3]"></div>
            <h1 className="text-3xl font-bold text-[#1A2B49] flex items-center">
              <span>
                Modal Offers Demo
              </span>
              <span className="ml-2 text-sm bg-[#F3F3F3] text-[#233D71] px-3 py-1 rounded-full font-medium">Interactive Demo</span>
            </h1>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Left Column - Product Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-lg shadow-md p-8"
          >
            <div className="flex flex-col items-center">
              <div className="text-8xl mb-8">{product.image}</div>
              <h2 className="text-3xl font-bold text-[#1A2B49] mb-4 text-center">{product.name}</h2>
              
              {/* Rating */}
              <div className="flex items-center space-x-2 mb-6">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <svg 
                      key={i}
                      className={`w-5 h-5 ${i < Math.floor(product.rating) ? "text-yellow-400" : "text-gray-300"}`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <span className="text-sm font-medium text-[#6E6D7A]">{product.rating} ({product.reviews} reviews)</span>
              </div>

              {/* Price and Features */}
              <div className="text-2xl font-bold text-[#FE4215] mb-8">${product.price}</div>
              
              <div className="w-full space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-[#233D71]" />
                  <span className="text-[#6E6D7A]">Noise cancellation technology</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-[#233D71]" />
                  <span className="text-[#6E6D7A]">40-hour battery life</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-[#233D71]" />
                  <span className="text-[#6E6D7A]">Bluetooth 5.2 connectivity</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-[#233D71]" />
                  <span className="text-[#6E6D7A]">Voice assistant compatible</span>
                </div>
              </div>

              {/* Add to cart button */}
              <button
                onClick={handleAddToCart}
                disabled={productAdded}
                className={`w-full py-4 px-6 rounded-lg font-medium text-white transition-all duration-300 flex items-center justify-center space-x-2
                  ${productAdded 
                    ? 'bg-[#233D71] cursor-default' 
                    : 'bg-[#F65C2A] hover:bg-[#FE4215]'}`
                }
              >
                {productAdded ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Added to Cart!</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    <span>Add to Cart</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Right Column - Try Demo Callout */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center"
          >
            {/* Demo Information Card - Following MomentScience Style Guide */}
            <div className="bg-white rounded-lg shadow-md p-8 w-full">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-[#F3F3F3] rounded-full flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-[#FE4215]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                
                <h3 className="text-2xl font-bold text-[#1A2B49] mb-4">Modal Offers</h3>
                
                <p className="text-[#6E6D7A] text-base mb-6 max-w-md">
                  MomentScience modal offers appear when users take specific actions, like adding items to their cart. This creates an opportunity to present personalized offers at key moments in the purchase journey.
                </p>
                
                <p className="text-[#6E6D7A] text-base mb-8 max-w-md">
                  Modal offers work best for high-intent actions where users have shown interest in making a purchase.
                </p>

                <div className="mt-4 flex flex-col items-center justify-center">
                  <button
                    onClick={() => {
                      const productSection = document.querySelector('.bg-white.rounded-lg button');
                      if (productSection) {
                        productSection.scrollIntoView({ behavior: 'smooth' });
                        setTimeout(() => {
                          const addToCartButton = document.querySelector('.bg-\\[\\#F65C2A\\]');
                          if (addToCartButton && addToCartButton instanceof HTMLElement) {
                            addToCartButton.classList.add('animate-pulse');
                            setTimeout(() => {
                              addToCartButton.classList.remove('animate-pulse');
                            }, 2000);
                          }
                        }, 500);
                      }
                    }}
                    className="px-6 py-3 bg-[#FE4215] text-white font-medium rounded-lg hover:bg-[#F65C2A] shadow-md transition-all duration-300 mb-4 flex items-center space-x-2"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    <span>Click "Add to Cart" to See Offers</span>
                  </button>
                  <div className="flex items-center justify-center mt-2">
                    <svg className="w-5 h-5 text-[#FE4215] mr-2 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                    <span className="text-sm text-[#6E6D7A]">Try the demo now</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Instructions Banner - Following MomentScience style guide */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 rounded-lg p-6 bg-[#F3F3F3]"
        >
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center text-center max-w-xl">
              <h3 className="text-lg font-bold text-[#1A2B49]">Note</h3>
              <p className="text-base mt-2 text-[#6E6D7A]">
                This is a demonstration of MomentScience modal offers. The offers shown are examples and may vary in a production environment.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Container for MomentScience content - Keep default positioning */}
      <div id="adpx" className="hidden">
        {/* The SDK will inject content here */}
      </div>
    </div>
  )
}

export default ModalOffersDemoPage